import { useMemo, useCallback, useState } from "react";
import { Radio } from "antd";
import { ArrowRightOutlined } from '@ant-design/icons';
import WithToolTip from "src/components/Common/WithToolTip";
import { snapshotFilters } from "src/constants/globalConstants"
import { getDefaultFormattedDate, getFormattedSeekValue, openFileInNewTab } from "src/utils/helperFunctions";
import { AI_PROCTORING } from "../../constant";

const SnapshotsContainer = ({
    isImgProctoringModal = true,
    criticalSnapshots,
    allSnapshots,
    isAiProctored,
    referenceImageLink,
}) => {
    const [currentFilter, setCurrentFilter] = useState("all")

    const getSnapshots = useCallback(() => {
        if (currentFilter === 'all')
            return allSnapshots
        else {
            return criticalSnapshots
        }
    }, [currentFilter, allSnapshots, criticalSnapshots])

    const displayedSnapshots = useMemo(() => {
        const snapshots = getSnapshots()
        if (referenceImageLink && currentFilter === 'all') {
            return [
                {
                    id: 'reference-image',
                    snapshotLink: referenceImageLink,
                    label: 'Reference Image',
                    isReferenceImage: true,
                },
                ...snapshots,
            ]
        }
        return snapshots
    }, [currentFilter, getSnapshots, referenceImageLink])
    /**
     * Get grid class based on array length and adjust modal width accordingly
     * @param {array} snapshots - Current snapshots length (allSnapshots/criticalSnapshots)
     * @returns {object} - With grid columns class and snapCriticalWidthStyle (required only when critical snapshots array empty)
     */
    const getGridColClassAndBodyWidth = (snapshots) => {
        const styleObj = {
            gridColsStyle: '',
            snapCriticalWidthStyle: ''
        };
        if (!snapshots.length) return styleObj;
        if (snapshots.length > 0)
            return {
                snapCriticalWidthStyle: `snap-critical-width-${snapshots.length > 4 ? 4 : snapshots.length}`,
                gridColsStyle: `grid-cols-${snapshots.length > 4 ? 4 : snapshots.length}`
            };
    };

    const { gridColsStyle, snapCriticalWidthStyle } = useMemo(
        () => getGridColClassAndBodyWidth(displayedSnapshots.length ? displayedSnapshots : allSnapshots),
        [allSnapshots, displayedSnapshots]
    );

    const handleOpenFile = (fileLink) => {
        openFileInNewTab(fileLink)
    }

    return (
        <>
            {
                isImgProctoringModal
                    ? <>
                        <p className={`text-14 clr-gray mt-20 text-center`}>{criticalSnapshots.length} out of {allSnapshots.length} images are critical</p>
                    </>
                    : <></>
            }
            <div className={isImgProctoringModal ? "flex flex-col items-center" : ""}>
                <div className={`flex mt-2 pt-0 ${!isImgProctoringModal ? "justify-center" : ""}`}>
                    {
                        isImgProctoringModal
                            ? snapshotFilters.map(filter => <button
                                key={filter.value}
                                className={`filter-btn b-20 mr-5 ${currentFilter === filter.value ? 'selected-filter-btn text-white' : ''} txt-14 weight-600`}
                                onClick={() => setCurrentFilter(filter.value)}
                            >
                                {filter.label} {filter.value === 'all' ? `(${allSnapshots.length})` : `(${criticalSnapshots.length})`}
                            </button>
                            )
                            : <Radio.Group
                                options={snapshotFilters.map(filter => ({
                                    value: filter.value,
                                    label: `${filter.label} ${filter.value === 'all'
                                        ? `(${allSnapshots.length})`
                                        : `(${criticalSnapshots.length})`}`
                                }))}
                                onChange={({ target: { value } }) => setCurrentFilter(value)}
                                value={currentFilter}
                                optionType="button"
                                buttonStyle="solid"
                            />
                    }
                </div>
                {
                    displayedSnapshots.length > 0 ? (
                        <div className={`grid ${isImgProctoringModal ? gridColsStyle : "grid-cols-6"} grid-gap-12 mt-3 mobile-grid-cols`}>
                            {
                                displayedSnapshots.map(snap =>
                                    <WithToolTip
                                        key={snap.id}
                                        showToolTip={(isAiProctored && snap.critical) || snap.hasReadinessTooltip}
                                        title={snap.criticalReason}
                                    >
                                        <div className='relative height-fit'>
                                            <div className="absolute top-0 left-0 snap-width snap-overlay" />
                                            <img src={snap?.media?.link ?? snap?.snapshotLink} alt='' className="snap-width" loading="lazy" />
                                            <p className="absolute bottom-0 left-0 p-10 mb-1 text-12 text-white">
                                                {
                                                    snap?.isReferenceImage ?
                                                        snap.label
                                                        : snap?.createdAt ?
                                                        `${getDefaultFormattedDate(snap?.createdAt, "lll")} IST`
                                                        : getFormattedSeekValue(snap?.timestamp)
                                                }
                                            </p>
                                            <span
                                                onClick={() => handleOpenFile(snap?.media?.link ?? snap?.snapshotLink)}
                                                className="absolute bottom-[10] right-[6] cursor-pointer"
                                            >
                                                <ArrowRightOutlined style={{ color: "#fff", transform: "rotate(-45deg)" }} />
                                            </span>
                                            {
                                                snap?.critical
                                                    ? <p className={`absolute txt-12 text-white critical-icon 
                                                        ${snap.criticalType === AI_PROCTORING.TYPES.CHEATING ? "critical-icon-err" : "critical-icon-warn"} select-none`}
                                                    >
                                                        !
                                                    </p>
                                                    : <></>
                                            }
                                        </div>
                                    </WithToolTip>
                                )
                            }
                        </div>
                    ) : (
                        <div className={`${snapCriticalWidthStyle} flex items-center justify-center text-18 weight-600 mt-3 clr-gray`}>
                            No snapshots found
                        </div>
                    )
                }
            </div>
        </>
    )
}

export default SnapshotsContainer
